import './index.css'
import { getPageXOffset, getPageYOffset } from '@disco/utils/dom'
import cn from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import InlineSVG from 'svg-inline-react'
import type { PopoverItemType } from 'types/ui'
import type { ButtonAppearance } from 'components/ui/button'
import Button from 'components/ui/button'
import Popover from 'components/ui/popover'
import useClickOutside from 'hooks/use-click-outside'
import { stateToHTML } from 'draft-js-export-html'
import Icon, { IconKey } from 'components/ui/icon'
type DropdownOption = PopoverItemType
type Props = {
  behavior?: 'button-dropdown' | 'dropdown'
  buttonSize?: 'default' | 'compact' | 'large'
  caption?: React.ReactNode
  className?: string
  popoverClassName?: string
  compact?: boolean
  disabled?: boolean
  hasArrow?: boolean
  icon?: IconKey
  options: DropdownOption[]
  tooltip?: string
  selected?: Pick<PopoverItemType, 'value'> | null | undefined
  appearance?: ButtonAppearance
  // TODO: Fix event handler types
  // DEPRECATED: Use props.options[].onClick instead of props.onOptionClick
  onOptionClick?: (value: any) => void
  onToggle?: () => void
  onOpen?: () => void
  right?: boolean
  'aria-label'?: string
}

const EllipsisMenu = ({
  behavior = 'dropdown',
  buttonSize,
  caption,
  className,
  popoverClassName,
  compact,
  disabled,
  hasArrow = true,
  icon,
  options,
  tooltip,
  onOptionClick,
  onToggle,
  onOpen,
  appearance: overwriteAppearance,
  selected,
  right,
  'aria-label': ariaLabel,
}: Props) => {
  const portalContainerRef = useRef<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const popoverRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    // TODO: do we still need such init? was only for storybook - remove useRef in follow-up
    portalContainerRef.current =
      document.getElementById('app') || document.getElementById('__next')
  }, [])
  const [popoverStyle, setPopoverStyle] = useState<
    | {
        top: number
        left: number
      }
    | null
    | undefined
  >()
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false)
  const setPopoverRef = useCallback(
    (node: HTMLElement | null) => {
      popoverRef.current = node
      const popover = node?.getBoundingClientRect()
      const container = containerRef.current?.getBoundingClientRect()

      if (popover && container) {
        let top

        if (
          container.top > popover.height &&
          container.bottom + popover.height > window.innerHeight
        ) {
          top = container.top - popover.height
        } else {
          top = container.bottom
        }

        setPopoverStyle({
          top: top + getPageYOffset(),
          left: right
            ? container.right + getPageXOffset()
            : container.right - popover.width + getPageXOffset(),
        })
      }
    },
    [containerRef, right],
  )
  const togglePopover = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) return

      if (onToggle) {
        onToggle()
      }
      if (onOpen && !popoverVisible) {
        onOpen()
      }

      setPopoverVisible(state => !state)
    },
    [disabled, onToggle],
  )
  const hidePopover = useCallback(() => {
    if (!popoverVisible) return

    if (popoverVisible && onToggle) {
      onToggle()
    }

    setPopoverVisible(false)
  }, [onToggle, popoverVisible])
  const handleOptionClick = useCallback(
    (option: DropdownOption) => {
      if (onOptionClick) {
        onOptionClick(option.value)
      }

      hidePopover()
    },
    [hidePopover, onOptionClick],
  )
  useClickOutside(popoverRef, hidePopover, true)
  const handleResize = useCallback(() => {
    hidePopover()
    window.removeEventListener('resize', handleResize)
  }, [hidePopover])
  useEffect(() => {
    if (popoverVisible) {
      window.addEventListener('resize', handleResize)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [popoverVisible])
  const popoverClass = cn('ellipsis-menu__dropdown', popoverClassName, {
    'ellipsis-menu__dropdown--closed': !popoverVisible,
    'ellipsis-menu__dropdown--open': popoverVisible,
  })
  const arrowDownIcon = (
    <div
      className="ellipsis-menu__arrow"
      onClick={behavior === 'button-dropdown' ? togglePopover : undefined}
    >
      <InlineSVG
        src={require('!svg-inline!assets/icons/arrow-down.svg')}
        width="24"
        height="24"
        raw
      />
    </div>
  )
  let buttonCaption = caption

  if (behavior === 'button-dropdown' && !caption) {
    buttonCaption = options[0]?.label
  }

  let link: PopoverItemType['link'] | null = null

  if (options[0]?.type === 'link') {
    link = options[0]?.link ?? null
  }

  let appearance: ButtonAppearance = buttonCaption ? 'subtle' : 'icon'

  if (overwriteAppearance) {
    appearance = overwriteAppearance
  }

  const renderPortal = (container: HTMLElement) => {
    return ReactDOM.createPortal(
      <div
        className={popoverClass}
        ref={setPopoverRef}
        style={{
          ...popoverStyle,
          minWidth: containerRef.current?.getBoundingClientRect().width,
        }}
      >
        <Popover
          selected={selected}
          compact={compact}
          items={behavior === 'dropdown' ? options : options.slice(1)}
          onClick={handleOptionClick}
        />
      </div>,
      container,
    )
  }

  return (
    <div
      className={cn('ellipsis-menu', className)}
      ref={containerRef}
      onClick={e => e.stopPropagation()}
    >
      <Button
        icon={icon}
        appearance={appearance}
        caption={buttonCaption}
        compact={buttonSize === 'compact'}
        large={buttonSize === 'large'}
        disabled={disabled}
        split={hasArrow ? arrowDownIcon : undefined}
        linkTarget={link?.target ? link.target : undefined}
        link={link ? link.url : undefined}
        linkDownload={link ? link.download : undefined}
        tooltip={tooltip}
        aria-label={ariaLabel || (!buttonCaption ? 'More actions' : undefined)}
        onClick={
          behavior === 'dropdown'
            ? togglePopover
            : () => {
                const firstOption = options[0]
                if (!firstOption) return

                handleOptionClick(firstOption)

                // When behavior !== 'dropdown' option[0].onClick is not called in Popover.
                // The original problem is that we have two places to handle option.onClick,
                // one piece of logic is in handleOptionClick + onOptionClick
                // and the other is in option.onClick.
                if (!firstOption.disabled && firstOption.onClick) {
                  firstOption.onClick()
                }
              }
        }
      />
      {popoverVisible &&
        portalContainerRef.current &&
        renderPortal(portalContainerRef.current)}
    </div>
  )
}

/**
 * @deprecated This component is deprecated. Please use one of the following alternatives:
 *
 * - For dropdown menus: Use `DropdownMenu` from `@components/ds`
 *   (import { DropdownMenu } from 'components/ds')
 * - For button-dropdown behavior: Use `SplitButton` from `@components/ds/SplitButton`
 *   (import { SplitButton } from 'components/ds/SplitButton')
 *
 * Migration guide:
 * - For `behavior="dropdown"`: Replace with DropdownMenu.Root, DropdownMenu.Trigger, DropdownMenu.Content, DropdownMenu.Item
 * - For `behavior="button-dropdown"`: Replace with SplitButton component
 *
 * @see {@link https://www.radix-ui.com/themes/docs/components/dropdown-menu} DropdownMenu documentation
 * @see SplitButton component for button-dropdown behavior
 *
 * A dropdown menu component that can behave as a simple dropdown or a button with dropdown options
 * @param behavior - The menu behavior: 'dropdown' or 'button-dropdown' (defaults to 'dropdown')
 * @param buttonSize - The size of the button: 'default', 'compact', or 'large'
 * @param caption - The text caption to display on the button
 * @param className - Additional CSS classes for the menu container
 * @param popoverClassName - Additional CSS classes for the popover dropdown
 * @param compact - Whether to use compact styling for the popover
 * @param disabled - Whether the menu is disabled (defaults to false)
 * @param hasArrow - Whether to show the dropdown arrow (defaults to true)
 * @param icon - The icon to display on the button
 * @param options - Array of menu options to display
 * @param tooltip - Tooltip text for the button
 * @param onOptionClick - Callback fired when an option is clicked (deprecated, use option.onClick instead)
 * @param onToggle - Callback fired when the menu is toggled
 * @param onOpen - Callback fired when the menu is opened
 * @param appearance - Override the button appearance
 * @param selected - The currently selected option
 * @param right - Whether to align the dropdown to the right
 * @returns A dropdown menu component with configurable behavior and styling
 */
export default React.memo<Props>(EllipsisMenu)
